import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Wifi, Lock, MessageCircle, ArrowRight, KeyRound, Users, Video, Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Change password state (forced on first login)
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [changingPwd, setChangingPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.must_change_password) {
        setTempToken(res.data.token);
        setCurrentPwd(password);
        setShowChangePassword(true);
        toast.info('You must change your password before continuing.');
        setLoading(false);
        return;
      }
      await login(email, password);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPwd.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPwd === currentPwd) {
      toast.error('New password must be different from current password');
      return;
    }
    setChangingPwd(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd
      }, { headers: { Authorization: `Bearer ${tempToken}` } });
      toast.success('Password changed successfully! Signing you in...');
      setShowChangePassword(false);
      await login(email, newPwd);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setForgotLoading(false);
    }
  };

  const features = [
    { icon: MessageCircle, title: 'Channels & DMs', desc: 'Organize conversations by team, project, or topic' },
    { icon: Video, title: 'Audio & Video Calls', desc: 'Multi-party group calls with screen sharing' },
    { icon: Users, title: 'Real-time Collaboration', desc: 'To-dos, reminders, events & file sharing' },
    { icon: Shield, title: 'Enterprise Security', desc: 'Admin controls, role management & audit trails' },
  ];

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left Brand Panel - Modern Design */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative overflow-hidden items-center justify-center">
        {/* Green gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(152,60%,36%)] via-[hsl(152,55%,30%)] to-[hsl(160,50%,22%)]" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-white/5 rounded-full" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-white/5 rounded-full" />
        
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />

        <div className="relative z-10 max-w-lg px-12">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-10">
            <img src="/imd-logo.svg" alt="IMD Logo" className="h-28 w-auto drop-shadow-lg" style={{filter: 'brightness(0) invert(1)'}} />
          </div>

          <h2 className="text-4xl font-bold mb-3 text-white leading-tight" style={{fontFamily: 'Space Grotesk'}}>
            Welcome to Tello
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10">
            Your team's communication hub. Real-time messaging, video calls, and collaboration tools — all in one powerful platform.
          </p>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-3">
            {features.map((f, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-4 transition-all duration-200 hover:bg-white/15" data-testid={`login-feature-${i}`}>
                <f.icon className="w-5 h-5 text-white/90 mb-2.5" />
                <h4 className="text-sm font-semibold text-white mb-1">{f.title}</h4>
                <p className="text-xs text-white/60 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Auth Panel */}
      <div className="flex-1 flex items-center justify-center p-6 relative">
        {/* Subtle background accent */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[hsl(var(--primary)/0.04)] rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        
        <div className="w-full max-w-[380px] relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/imd-logo.svg" alt="IMD Logo" className="h-14 w-auto" />
            <span className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>Tello</span>
          </div>

          {/* Desktop header */}
          <div className="hidden lg:block mb-8">
            <h1 className="text-2xl font-bold tracking-tight mb-1" style={{fontFamily: 'Space Grotesk'}}>Sign in to Tello</h1>
            <p className="text-muted-foreground text-sm">Enter your credentials to access your workspace</p>
          </div>

          <Card className="border border-border shadow-[var(--shadow-md)]" data-testid="login-card">
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="you@company.com" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    required 
                    data-testid="login-email"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <button 
                      type="button" 
                      className="text-xs text-[hsl(var(--primary))] hover:underline font-medium" 
                      onClick={() => { setForgotEmail(email); setShowForgot(true); setForgotSent(false); }} 
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input 
                    id="password" 
                    type="password" 
                    placeholder="Enter your password" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required 
                    data-testid="login-password"
                    className="h-11"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 text-sm font-semibold" 
                  disabled={loading} 
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">Sign In <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Powered by <span className="font-medium text-foreground">IMD</span> &middot; Secure enterprise communication
          </p>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="max-w-sm" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Space Grotesk'}}>Forgot Password</DialogTitle>
            <DialogDescription>Enter your email and we'll notify your administrator to reset your password.</DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[hsl(var(--presence-online)/0.15)] flex items-center justify-center mx-auto mb-3">
                <KeyRound className="w-6 h-6 text-[hsl(var(--presence-online))]" />
              </div>
              <p className="text-sm font-medium mb-1">Request Submitted</p>
              <p className="text-xs text-muted-foreground">If the email is registered, your administrator has been notified. They will reset your password and you'll be able to log in with a new one.</p>
              <Button className="mt-4" variant="outline" onClick={() => setShowForgot(false)}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input type="email" placeholder="you@company.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowForgot(false)}>Cancel</Button>
                <Button type="submit" disabled={forgotLoading} data-testid="forgot-submit-button">
                  {forgotLoading ? 'Sending...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Forced Password Change Dialog */}
      <Dialog open={showChangePassword} onOpenChange={() => {}}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Space Grotesk'}} className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[hsl(var(--primary))]" /> Change Your Password
            </DialogTitle>
            <DialogDescription>You must set a new password before continuing. This is required for security.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" placeholder="Enter new password (min 6 chars)" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" placeholder="Confirm new password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required data-testid="confirm-password-input" />
            </div>
            <Button type="submit" className="w-full" disabled={changingPwd} data-testid="change-password-button">
              {changingPwd ? 'Changing...' : 'Set New Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
